'use client';

import React from 'react';
import Link from 'next/link';
import Cookies from 'js-cookie';
import { useGeolocation } from '@/context/GeolocationContext';

const updateGtagConsent = (granted: boolean) => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('consent', 'update', {
      ad_storage: granted ? 'granted' : 'denied',
      ad_user_data: granted ? 'granted' : 'denied',
      ad_personalization: granted ? 'granted' : 'denied',
      analytics_storage: granted ? 'granted' : 'denied',
    });
  }
};

// SSR-safe read of the stored `localConsent` cookie. Returns undefined on the
// server and the first client render so hydration matches, then the cookie
// value on the client. Avoids reading a browser value with a lazy initializer
// (which would run during SSR).
const subscribeNoop = () => () => {};
const getConsentCookieSnapshot = () => Cookies.get('localConsent');
const getConsentCookieServerSnapshot = () => undefined;

const useCookieConsent = () => {
  // Use shared geolocation context to avoid duplicate API calls
  const {
    country: userRegion,
    isGDPR: isGDPRRegion,
    isLoaded: regionLoaded,
  } = useGeolocation();

  const storedConsent = React.useSyncExternalStore(
    subscribeNoop,
    getConsentCookieSnapshot,
    getConsentCookieServerSnapshot,
  );
  const hasStoredChoice = storedConsent !== undefined;

  // Local override applied when the user clicks Accept/Reject in this session,
  // so the banner dismisses immediately without waiting on a cookie re-read.
  const [interaction, setInteraction] = React.useState<boolean | null>(null);
  const hasUserInteracted = hasStoredChoice || interaction !== null;

  // Derive consent and banner visibility during render (no setState-in-effect).
  // First visit: GDPR -> denied default, others -> granted default; banner is
  // always shown for transparency/opt-out. Returning visitor: respect cookie,
  // hide the banner.
  const consent: boolean | null = !regionLoaded
    ? null
    : interaction !== null
      ? interaction
      : hasStoredChoice
        ? storedConsent === 'true'
        : !isGDPRRegion;
  const showBanner = regionLoaded && !hasStoredChoice && interaction === null;

  // Apply the consent state to gtag. These are external-system updates (not
  // setState), so running them in an effect is correct and not flagged. The
  // GDPR/non-GDPR behavior matches the original exactly.
  React.useEffect(() => {
    if (!regionLoaded) {
      return;
    }
    if (interaction !== null) {
      return;
    } // button handlers already updated gtag

    if (hasStoredChoice) {
      // Returning visitor - respect stored preference.
      updateGtagConsent(storedConsent === 'true');
    } else {
      // First visit - region-specific default (denied for GDPR, granted else).
      updateGtagConsent(!isGDPRRegion);
    }
  }, [regionLoaded, isGDPRRegion, hasStoredChoice, storedConsent, interaction]);

  const acceptCookie = () => {
    setInteraction(true);
    Cookies.set('localConsent', 'true', { expires: 365, sameSite: 'lax' });

    // Update Google Consent Mode v2 when user accepts
    updateGtagConsent(true);
  };

  const rejectCookie = () => {
    setInteraction(false);
    Cookies.set('localConsent', 'false', { expires: 365, sameSite: 'lax' });

    // Ensure consent remains denied
    updateGtagConsent(false);
  };

  return {
    consent,
    showBanner,
    userRegion,
    isGDPRRegion,
    hasUserInteracted,
    acceptCookie,
    rejectCookie,
  };
};

export const CookieConsent = () => {
  const { showBanner, isGDPRRegion, acceptCookie, rejectCookie } =
    useCookieConsent();

  if (!showBanner) {
    return null;
  }

  return (
    <div className="relative inset-0 z-10">
      <div className="fixed bottom-0 left-0 right-0 flex items-center justify-center bg-menu-gradient px-4 py-2 animate-slide-up">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between w-full max-w-6xl">
          <span className="text-white text-sm mb-2 sm:mb-0 sm:mr-4">
            {isGDPRRegion ? (
              // GDPR region messaging - explicit consent required
              <>
                We use cookies and similar technologies to enhance your
                experience, analyze site usage, and assist with marketing
                efforts. By clicking &quot;Accept&quot;, you consent to our use
                of cookies.{' '}
                <Link
                  href="/privacy-policy"
                  className="underline hover:no-underline"
                >
                  Privacy Policy
                </Link>
              </>
            ) : (
              // Non-GDPR region messaging - opt-out model
              <>
                We use cookies to ensure that we give you the best experience on
                our website. You can opt out at any time.{' '}
                <Link
                  href="/privacy-policy"
                  className="underline hover:no-underline"
                >
                  Privacy Policy
                </Link>
              </>
            )}
          </span>
          <div className="flex flex-col sm:flex-row gap-2">
            <button
              className="py-2 px-4 button-bordered text-sm"
              onClick={acceptCookie}
              aria-label="Accept cookies"
            >
              {isGDPRRegion ? 'Accept' : 'Continue'}
            </button>
            {/* Only show reject/opt-out button in GDPR regions */}
            {isGDPRRegion && (
              <button
                className="py-2 px-4 bg-transparent border border-white text-white hover:bg-white hover:text-black transition-colors text-sm"
                onClick={rejectCookie}
                aria-label="Reject cookies"
              >
                Reject
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
